import { useTranslation } from 'react-i18next';
import * as THREE from 'three';

interface Entity {
  type: string;
  vertices: THREE.Vector3[];
  nextIndex: number;
}

export class DXFParser {
  private entities: Entity[];

  constructor() {
    this.entities = [];
  }

  parse(dxfContent: string): THREE.BufferGeometry {
    const lines = dxfContent.split('\n');
    let i = 0;
    
    while (i < lines.length) {
      const line = lines[i].trim();
      
      if (line === '0' && i + 1 < lines.length && lines[i + 1].trim() === '3DFACE') {
        const face = this.parse3DFace(lines, i + 2);
        if (face) {
          this.entities.push(face);
        }
        i = face ? face.nextIndex : i + 1;
      } else {
        i++;
      }
    }
    
    return this.createGeometry();
  }

  private parse3DFace(lines: string[], startIndex: number): Entity | null {
    const vertices: THREE.Vector3[] = [];
    let i = startIndex;
    
    while (i < lines.length && vertices.length < 4) {
      const code = lines[i].trim();
      
      if (['10', '11', '12', '13'].includes(code)) {
        const x = parseFloat(lines[i + 1]);
        const y = parseFloat(lines[i + 3]);
        const z = parseFloat(lines[i + 5]);
        vertices.push(new THREE.Vector3(x, z, y));
        i += 6;
      } else {
        i++;
      }
    }
    
    if (vertices.length === 4) {
      return {
        type: '3DFACE',
        vertices,
        nextIndex: i
      };
    }
    
    return null;
  }

  private createGeometry(): THREE.BufferGeometry {
    const geometry = new THREE.BufferGeometry();
    const vertices: number[] = [];
    const indices: number[] = [];
    let vertexIndex = 0;

    this.entities.forEach(entity => {
      if (entity.type === '3DFACE') {
        vertices.push(
          ...entity.vertices[0].toArray(),
          ...entity.vertices[1].toArray(),
          ...entity.vertices[2].toArray(),
          ...entity.vertices[3].toArray()
        );

        indices.push(
          vertexIndex, vertexIndex + 1, vertexIndex + 2,
          vertexIndex + 2, vertexIndex + 3, vertexIndex
        );

        vertexIndex += 4;
      }
    });

    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();

    return geometry;
  }

  static async loadDXF(file: File): Promise<THREE.BufferGeometry> {
    const { t } = useTranslation();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const parser = new DXFParser();
          const geometry = parser.parse(e.target?.result as string);
          resolve(geometry);
        } catch (error) {
          console.error(t('errors.dxf.loadError'), error);
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsText(file);
    });
  }
}